import { useApi } from '@hero/context';

import type {
  UsePaymentBankTransferApi,
  UsePaymentCheckoutApi,
  UsePaymentStatusApi,
} from './types';
import { useMutationAPI } from '../../utils';
import { ENTITY_CONFIGS } from '../../config';

/** Query keys to invalidate when a payment is created (invoice list, wallet balance, cashback). */
const PAYMENT_RELATED_KEYS = [
  ...ENTITY_CONFIGS.invoice.queryKeyPatterns,
  ...ENTITY_CONFIGS.wallet.queryKeyPatterns,
  ...ENTITY_CONFIGS.cashback.queryKeyPatterns,
  ...ENTITY_CONFIGS.transaction.queryKeyPatterns,
  ...ENTITY_CONFIGS.unpaidTotal.queryKeyPatterns,
].map(key => [key] as readonly string[]);

/**
 * Create bank transfer payment. Uploads proof of transaction and creates payment.
 * Automatically invalidates invoice, wallet, and cashback queries on success.
 * API from ApiProvider (useApi('customer')); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UsePaymentBankTransferApi}
 */
export const usePaymentBankTransferApi: UsePaymentBankTransferApi = apiConfig => {
  const api = useApi('customer');
  return useMutationAPI({
    entity: 'payment',
    apiFn: api.payment.createBankPayment,
    apiConfig: {
      ...apiConfig,
      invalidateQueryKeys: [...PAYMENT_RELATED_KEYS, ...(apiConfig?.invalidateQueryKeys ?? [])],
    },
  });
};

/**
 * Create payment checkout (HyperPay). Returns checkout ID for payment processing.
 * API from ApiProvider (useApi('customer')); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UsePaymentCheckoutApi}
 */
export const usePaymentCheckoutApi: UsePaymentCheckoutApi = apiConfig => {
  const api = useApi('customer');
  return useMutationAPI({
    entity: 'payment',
    apiFn: api.payment.createCheckout,
    apiConfig: {
      invalidationStrategy: 'none',
      ...apiConfig,
    },
  });
};

/**
 * Check payment status after checkout. Returns payment result from gateway.
 * API from ApiProvider (useApi('customer')); pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UsePaymentStatusApi}
 */
export const usePaymentStatusApi: UsePaymentStatusApi = apiConfig => {
  const api = useApi('customer');
  return useMutationAPI({
    entity: 'payment',
    apiFn: api.payment.checkPayment,
    apiConfig: {
      invalidationStrategy: 'none',
      ...apiConfig,
    },
  });
};
